/* ============================================================================
   Farmkart Pincode Estimator — frontend controller
   Snippet : snippets/pincode-estimator.liquid
   Styles  : assets/pincode-widget.css
   API     : GET /apps/partial-pay/shipping?pincode=NNNNNN&weight=W
   Storage : localStorage key 'fk_pincode'

   UI states (set as data-state on #fk-pincode-widget):
     empty           — initial, no saved pincode
     loading         — fetch in flight
     success         — serviceable + rate returned
     not_serviceable — pincode not in ShipRocket network
     invalid         — client-side regex fail (not 6 digits)
     api_error       — network / server error (graceful fallback)

   FacebookApp Chromium 78+ safe:
     - var declarations (no let/const edge cases in very old webviews)
     - no ?. optional chaining
     - no ?? nullish coalescing
     - Promise.all / fetch supported in Chromium 78+
   ============================================================================ */
(function () {
  'use strict';

  var CFG                     = window.FK_PINCODE_CFG || {};
  var API_BASE                = CFG.apiBase || '/apps/partial-pay';
  var WEIGHT_KG               = CFG.weightKg || 0.5;
  var FREE_SHIP_THRESHOLD_INR = 1499;
  var LS_KEY                  = 'fk_pincode';

  // ── DOM refs ───────────────────────────────────────────────────────────────
  var root, form, input, btn, changeBtn;
  var cityEl, etaEl, rateEl, freeShipEl, codEl;

  // ── State machine ──────────────────────────────────────────────────────────
  function setState(state, data) {
    if (!root) return;
    root.setAttribute('data-state', state);

    var loading = (state === 'loading');
    if (input) input.disabled = loading;
    if (btn)   btn.disabled   = loading;

    if (state === 'success' && data) {
      if (cityEl) cityEl.textContent = data.city || '';

      if (etaEl) {
        etaEl.textContent = (data.etaMin === data.etaMax)
          ? String(data.etaMin)
          : (data.etaMin + '–' + data.etaMax);
      }

      if (rateEl) rateEl.textContent = String(data.rateInr || 0);

      if (freeShipEl) {
        if (data.freeShipEligible) {
          freeShipEl.innerHTML = ((window.FK_PINCODE_CFG && window.FK_PINCODE_CFG.checkIconSvg) || '') + ' Free shipping for this order';
          freeShipEl.setAttribute('data-free-ship', 'yes');
        } else if (data.amountToFreeShipInr > 0) {
          freeShipEl.textContent = 'Add ₹' + Number(data.amountToFreeShipInr).toLocaleString('en-IN') + ' more for FREE shipping';
          freeShipEl.setAttribute('data-free-ship', 'no');
        } else {
          freeShipEl.textContent = '';
          freeShipEl.removeAttribute('data-free-ship');
        }
      }

      if (codEl) {
        if (data.codAvailable === true) {
          codEl.innerHTML = ((window.FK_PINCODE_CFG && window.FK_PINCODE_CFG.checkIconSvg) || '') + ' Cash on delivery available — pay ₹150 online now, balance on delivery.';
          codEl.hidden = false;
        } else if (data.codAvailable === false) {
          codEl.textContent = 'This pincode is prepaid-only — full payment online.';
          codEl.hidden = false;
        } else {
          codEl.textContent = '';
          codEl.hidden = true;
        }
      }
    }
  }

  // ── Fetch + process ────────────────────────────────────────────────────────
  function checkPincode(pincode) {
    pincode = String(pincode || '').trim();

    if (!/^\d{6}$/.test(pincode)) {
      setState('invalid');
      return;
    }

    setState('loading');

    try { localStorage.setItem(LS_KEY, pincode); } catch (e) { /* private mode */ }

    var shippingUrl = API_BASE + '/shipping?pincode=' + pincode + '&weight=' + WEIGHT_KG;

    Promise.all([
      fetch(shippingUrl).then(function (r) { return r.json(); }),
      fetch('/cart.js').then(function (r) { return r.json(); }).catch(function () { return { total_price: 0 }; })
    ]).then(function (results) {
      handleResult(results[0], (results[1] && results[1].total_price) || 0);
    }).catch(function () {
      setState('api_error');
    });
  }

  function handleResult(shipping, cartTotalPaise) {
    if (!shipping || !shipping.ok) {
      var reason = (shipping && (shipping.error || shipping.reason)) || '';
      if (reason === 'invalid_pincode') {
        setState('invalid');
      } else if (reason === 'not_serviceable') {
        setState('not_serviceable');
      } else {
        setState('api_error');
      }
      return;
    }

    var cartTotalInr         = Math.round(cartTotalPaise / 100);
    var amountToFreeShipInr  = Math.max(0, FREE_SHIP_THRESHOLD_INR - cartTotalInr);
    var freeShipEligible     = (cartTotalInr >= FREE_SHIP_THRESHOLD_INR);

    setState('success', {
      city:                shipping.city || '',
      etaMin:              shipping.eta_min_days || 0,
      etaMax:              shipping.eta_max_days || 0,
      rateInr:             shipping.shipping_rate_inr || 0,
      freeShipEligible:    freeShipEligible,
      amountToFreeShipInr: amountToFreeShipInr,
      codAvailable:        shipping.cod_available
    });
  }

  // ── Init ───────────────────────────────────────────────────────────────────
  function init() {
    root      = document.getElementById('fk-pincode-widget');
    form      = document.getElementById('fk-pincode-form');
    input     = document.getElementById('fk-pincode-input');
    btn       = document.getElementById('fk-pincode-btn');
    changeBtn = document.getElementById('fk-pincode-change');

    if (!root || !form || !input || !btn) return;

    cityEl     = root.querySelector('[data-pincode-city]');
    etaEl      = root.querySelector('[data-pincode-eta]');
    rateEl     = root.querySelector('[data-pincode-rate]');
    freeShipEl = root.querySelector('[data-pincode-free-ship]');
    codEl      = root.querySelector('[data-pincode-cod]');

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      checkPincode(input.value);
    });

    if (changeBtn) {
      changeBtn.addEventListener('click', function () {
        setState('empty');
        input.value = '';
        input.focus();
      });
    }

    // Priority: 1) logged-in customer's saved address  2) localStorage
    var accountPin = (CFG.accountPincode && /^\d{6}$/.test(String(CFG.accountPincode).trim()))
      ? String(CFG.accountPincode).trim()
      : '';
    var lsPin = '';
    try { lsPin = localStorage.getItem(LS_KEY) || ''; } catch (e) { /* private mode */ }

    var autoPin = accountPin || ((/^\d{6}$/.test(lsPin)) ? lsPin : '');
    if (autoPin) {
      input.value = autoPin;
      checkPincode(autoPin);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
